<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div class="grid">
      <div
        id="product-gallery"
        class="col xs-span l6 offset-left-l offset-right-l"
        data-sign-post-location="main"
        js-sign-post
      >
        <product-gallery
          class="main-product__gallery"
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          :loading="state === 'loading'"
          :product="product || liquid"
          :variant="variant"
          :issu-name="issuName"
          :display-new-issu-button="displayNewIssuButton"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </div>

      <div class="main-product__content-box col xs-span l6 offset-left-l">
        <div class="main-product__details">
          <product-badge :product="product" />

          <h1
            id="template-title"
            class="
              main-product__title
              text-heading-xl
              text-heading-3xl-desktop
            "
            v-text="product.title || liquid.title"
          />

          <btn
            v-if="displayNewIssuButton && issuName"
            class="main-product__issu-button"
            class-label="text-body-xs-regular"
            modifiers="fill"
            :label="$string('product.read_sample_chapter')"
            show-icon
            @click="handleIssuClick"
          >
            <template #icon>
              <icon-issu />
            </template>
          </btn>

          <div
            v-if="productSeries.seriesTitle || product.type"
            class="main-product__series-and-type mt-m"
          >
            <p
              v-if="productSeries.seriesTitle"
              class="text-body-s-bold"
              v-text="productSeries.seriesTitle"
            />

            <span
              v-if="productSeries.seriesTitle && product.type"
              class="mx-2xs"
            >
              /
            </span>

            <p
              v-if="product.type"
              class="text-body-s-bold"
              v-text="product.type"
            />
          </div>

          <div class="main-product__description-wrapper">
            <btn
              v-if="purifiedDescription?.length > 150"
              class="main-product__description-show-more"
              modifiers="text"
              class-label="text-body-s-regular"
              :label="isDescriptionExpanded
                ? $string('accessibility.close')
                : $string('accessibility.read_more')"
              @click="toggleDescription"
            />

            <div
              v-if="purifiedDescription?.length > 150"
              class="main-product__description-toggle"
              :class="isDescriptionExpanded
                ? 'main-product__description--expanded'
                : 'main-product__description--closed'"
              :style="{ maxHeight: `${descriptionHeight}px` }"
            >
              <div
                ref="description"
                class="main-product__description text-body-s-regular rte mt-2xs"
                v-html="product.description || liquid.description"
              />
            </div>

            <div
              v-else
              ref="description"
              class="main-product__description text-body-s-regular rte mt-2xs"
              v-html="product.description || liquid.description"
            />
          </div>

          <product-prices
            v-if="showSchoolPrice"
            :price-label="$string('product.prices.school')"
            class="main-product__school-prices full-width mt-m"
            class-label="text-body-l-bold"
            class-price="main-product__school-prices--price text-body-l-bold"
            class-compare-at-price="main-product__school-prices--compare-price text-other-sale-l"
            :compare-at-price="schoolCompareAtPrice"
            :price="schoolPrice"
          />

          <product-prices
            class="main-product__prices-container"
            :class="showSchoolPrice ? 'main-product__prices-container--school same-color' : 'mt-m'"
            :class-label="showSchoolPrice ? 'text-body-s-regular' : 'text-body-l-bold'"
            :class-price="showSchoolPrice ? 'text-body-s-bold' : 'text-body-l-bold'"
            :class-compare-at-price="showSchoolPrice ? 'text-other-sale-s' : 'text-body-l-bold'"
            :price-label="showSchoolPrice ? $string('product.prices.retail') : ''"
            :compare-at-price="variant.compareAtPrice?.amount"
            :loading="state === 'loading'"
            :price="variant.price?.amount || liquid.selectedOrFirstAvailableVariant?.price?.amount"
            :varies="variant.price?.varies || liquid.selectedOrFirstAvailableVariant?.price?.varies"
          />
        </div>

        <product-form
          class="main-product__form"
          :loading="state === 'loading'"
          :product="product || liquid"
          :overridden-button="overriddenButton"
          :section="section"
          :variant="variant"
          :cart-items="cartItems"
          :is-personalized="isPersonalized"
          @update-variant="handleVariantUpdate"
        />

        <product-evaluation-copy
          v-if="variant && evalCopy && product.publishedProduct"
          :variant="variant"
          :cart-items="cartItems"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Boolean} displayNewIssuButton - Display new issu button.
 * @param {Object} productSeries - Product series.
 * @param {Object} section - Section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ProductForm from '~async/product-form/product-form'
import ProductBadge from '~async/product-badge/product-badge'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductEvaluationCopy from '~async/product-evaluation-copy/product-evaluation-copy'
import ProductPrices from '~global/product-prices/product-prices'

import IconIssu from '~icons/general/issu.svg'

import productQuery from '~graphql/product.gql'
import { formatProduct } from '~/helpers/format'
import { emitUpdateRequestObject } from '~/helpers/vue'
import timings from '~/config/timings'

export default {
  name: 'MainProduct',

  components: {
    IconIssu,
    Btn,
    ProductForm,
    ProductGallery,
    ProductPrices,
    ProductBadge,
    ProductEvaluationCopy,
  },

  props: {
    issuName: {
      type: String,
      default: '',
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    overriddenButton: {
      type: [Boolean, Object],
      default: () => false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    isPersonalized: {
      type: Boolean,
      default: false,
    },
    displayNewIssuButton: {
      type: Boolean,
      default: false,
    },
    productSeries: {
      type: [Object, String],
      default: () => ({
        seriesTitle: '',
        seriesSequenceNumber: '',
        collectionName: '',
      }),
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayQuantitySelector: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        id: '',
        initialSelect: true,
        resetFormBehaviour: 'disabled',
        selectDisabledValues: true,
      }),
    },
    evalCopy: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      currentIndex: 0,
      currentNamespace: 'gallery',
      handle: cnvs.page.handle,
      state: 'loading',
      descriptionHeight: this.isMobile ? 95 : 52.4,
      isDescriptionExpanded: false,
      cartItems: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStateLabel: (state) => state.cart.status.label,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns the customer role.
     * @return {String}
     */
    customerRole() {
      return this.cart?.attributes?.customer_role
    },

    /**
     * Returns the school price.
     * @return {Number|Boolean}
     */
    schoolPrice() {
      return this.getTagPrice('priceschool-')
    },

    /**
     * Returns the school compare at price.
     * @return {Number|Boolean}
     */
    schoolCompareAtPrice() {
      return this.getTagPrice('comparepriceschool-')
    },

    /**
     * Compute if the school price should be shown.
     * @returns {Boolean}
     */
    showSchoolPrice() {
      return this.schoolPrice &&
        this.customerRole === this.$string('general.multi_role_switcher.teacher_value') &&
        this.schoolPrice !== this.variant.price?.amount &&
        this.schoolPrice !== this.liquid.selectedOrFirstAvailableVariant?.price?.amount
    },

    /**
     * Returns the product description without HTML tags or special characters.
     * @return {String}
     */
    purifiedDescription() {
      /* eslint-disable */
      return this.product?.description?.replace(/<\/style>/gi, '')
        .replace(/<(.*?) on(.*?)=['"](.*?)>/gi, '')
        .replace(/(<meta |<style|<\/*script)(.*?)>/gi, '')
        .replace(/(<([^>]+)>)/ig, '')
        .replace(/<(.*?) (href|src)="j(.*)a(.*)v(.*)a(.*)s(.*)c(.*)r(.*)i(.*)p(.*)t(.*):(.*?)>/gi, '')
      /* eslint-enable */
    },
  },

  watch: {

    /**
     * Watch handle for update to load new product.
     */
    handle() {
      this.queryProduct(this.handle)
    },

    isMobile() {
      this.descriptionHeight = this.isMobile ? 95 : 52.4
    },

    /**
     * Watch cart items updated checking state label.
     */
    cartStateLabel() {
      if (this.cartStateLabel === 'updated') {
        this.cartItems = this.cart?.items
      }
    },
  },

  mounted() {
    this.setProduct()
    this.cartItems = this.cart?.items
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      if (!this.liquid.handle) {
        this.queryProduct()
        return
      }

      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false || this.product.handle === handle) {
        return
      }

      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query: productQuery,
          variables: {
            country: cnvs.store.country,
            handle,
            language: cnvs.store.language,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.$emit('update-request-object', {
          response: formatProduct(response),
          type: 'product',
        })

        this.state = 'ready'

      } catch (error) {
        cnvs.ReportError('Failed to load product', error)
      }
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Get tag price.
     * @param {String} [tagPrefix] - Tag prefix.
     * @returns {Number|Boolean}
     */
    getTagPrice(tagPrefix = '') {
      const dataTag = this.product?.tags?.find(tag => tag.startsWith(tagPrefix))
      const data = dataTag?.split(tagPrefix).pop()

      if (isNaN(data)) {
        return false
      }

      return data * 100
    },

    /**
     * Toggle description area.
     */
    async toggleDescription() {
      const mobileDescription = document.querySelector('.product-breakdown__description-wrapper--mobile')
      const desktopDescription = document.querySelector('.product-breakdown__description-wrapper--desktop')
      const productBreakdownDescription = window.getComputedStyle(mobileDescription, null).display === 'none'
        ? desktopDescription
        : mobileDescription

      if (productBreakdownDescription) {
        window.scroll(0, productBreakdownDescription.getBoundingClientRect().top)

        return
      }

      if (this.isDescriptionExpanded) {
        this.descriptionHeight = this.isMobile ? 95 : 52.4

        setTimeout(() => {
          this.isDescriptionExpanded = !this.isDescriptionExpanded
        }, timings.normal)
        return
      }

      this.isDescriptionExpanded = !this.isDescriptionExpanded
      await this.$nextTick()
      this.descriptionHeight = this.$refs.description.getBoundingClientRect().height
    },

    /**
     * Open issu modal overlay.
     */
    handleIssuClick() {
      this.openOverlay({
        component: 'issu-modal',
        ignoreDismissed: true,
        namespace: 'issuModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
</style>
